<script lang="ts" setup>
import { useWatchErrorHandler } from '@errors/useErrorHandler';
import { SUREbusinessCommonDomainSoortVerzekeringEnum as SoortVerzekering } from '@services/polis-public/orval-api/model/sUREbusinessCommonDomainSoortVerzekeringEnum';
import VInfoBox from '@shared/component/VInfoBox/VInfoBox.vue';
import { useQuery } from '@tanstack/vue-query';
import { computed, toRefs } from 'vue';
import StringField from '@shared/form/StringField/StringField.vue';
import FieldRowV2 from '@shared/form/FieldRowV2/FieldRowV2.vue';
import KentekenField from '@shared/form/KentekenField/KentekenField.vue';
import VCurrencyField from '@shared/form/VCurrencyField/VCurrencyField.vue';
import FieldRowGroup from '@shared/form/FieldRowV2/FieldRowGroup.vue';
import { currencyFormatter } from '@tools/helpers';
import { tryFormatDate } from '@tools/dates';
import { polisQueries } from '../polisQueries';

const props = defineProps<{
  polisnummer: string;
}>();

const { polisnummer } = toRefs(props);
// todo: Vervangen voor controle op Productsoort zodra we Productsoort 'Beurs' vervangen hebben met 'Beurs Faciliteit' en 'Beurs Open Broked'.
const isBeursPolis = computed(() => polisnummer.value.substring(0, 3) === 'SBB');

const { data } = useQuery({
  ...polisQueries.getDetails(polisnummer),
  select: (res) => res.data,
  staleTime: 60000,
});

const dekking = computed(() =>
  data?.value?.polis?.subpolis?.length
    ? data.value.polis.subpolis[0][data.value.polis.soortverzekering.toLowerCase()][0]
    : undefined,
);

const { data: gebouwOms, error } = useQuery({
  ...polisQueries.getGebouwomschrijving(dekking.value?.gebouwcode),
  select: (res) => res.data,
  enabled: computed(
    () => data?.value?.polis?.soortverzekering === SoortVerzekering.BR.toUpperCase(),
  ),
});

useWatchErrorHandler(error);
</script>
<template>
  <div>
    <VInfoBox type="info" v-if="data.polis.soortverzekering === SoortVerzekering.PP">
      Voor een pakketpolis zijn geen branchegegevens beschikbaar.
    </VInfoBox>
    <FieldRowGroup v-else :gap="2">
      <FieldRowV2 label="SBI-code">
        <StringField readonly :value="`${dekking.sbicode ?? ''} - ${dekking.omssbicode ?? ''}`" />
      </FieldRowV2>
      <FieldRowV2 label="Hoedanigheid">
        <StringField readonly :value="dekking.omshoedanigheidscode" />
      </FieldRowV2>
      <template
        v-if="
          [SoortVerzekering.MR.toUpperCase(), SoortVerzekering.VR.toUpperCase()].includes(
            data.polis.soortverzekering,
          )
        "
      >
        <FieldRowV2 label="Kenteken">
          <span v-if="!dekking.kenteken">-</span>
          <KentekenField
            v-else
            readonly
            :value="dekking.kenteken"
            :additional-data="{
              merk: dekking.merk,
              model: dekking.model,
              bouwjaar: dekking.bouwjaar,
            }"
          />
        </FieldRowV2>
        <FieldRowV2 label="Meldcode">
          <StringField readonly :value="dekking.rdwmeldcode" />
        </FieldRowV2>
        <FieldRowV2 label="Gewicht">
          <StringField readonly :value="dekking.gewicht" />
        </FieldRowV2>
        <FieldRowV2 label="Kilometrage">
          <StringField readonly :value="dekking.omskilometrage" />
        </FieldRowV2>
        <FieldRowV2 label="Catalogusprijs excl. BTW">
          <VCurrencyField readonly :value="dekking.catalogusprijsexclusiefbtw" />
        </FieldRowV2>
        <FieldRowV2 label="Schadevrije jaren">
          <StringField readonly :value="dekking.bm?.schadevrijejaren" />
        </FieldRowV2>
        <FieldRowV2 label="Trede">
          <StringField readonly :value="dekking.bm?.trede" />
        </FieldRowV2>
        <FieldRowV2 label="Kortingspercentage">
          <StringField readonly :value="dekking.bm?.bmpercentage" />
        </FieldRowV2>
      </template>

      <template v-if="data.polis.soortverzekering === SoortVerzekering.AB.toUpperCase()">
        <FieldRowV2 label="Soort bedrijf">
          <StringField readonly :value="dekking.omsbedrijfscode" />
        </FieldRowV2>
        <FieldRowV2 label="Verzekerdbedrag A">
          <VCurrencyField readonly :value="dekking.verzekerdbedraga" />
        </FieldRowV2>
        <FieldRowV2 label="Verzekerdbedrag B">
          <VCurrencyField readonly :value="dekking.verzekerdbedragb" />
        </FieldRowV2>
        <FieldRowV2 label="Dekkingsgebied">
          <StringField readonly :value="dekking.dekkingsgebiedcode" />
        </FieldRowV2>
        <template v-if="isBeursPolis">
          <FieldRowV2 label="Minimum premie">
            {{
              data.polis.minimumpremie
                ? `${currencyFormatter(data.polis.minimumpremie)} op termijnbasis`
                : '-'
            }}
          </FieldRowV2>

          <FieldRowV2 label="Jaaromzet">
            {{ data.polis.jaaromzet ? currencyFormatter(data.polis.jaaromzet) : '-' }}
          </FieldRowV2>
        </template>
      </template>
      <template v-if="data.polis.soortverzekering === SoortVerzekering.BR.toUpperCase()">
        <FieldRowV2 label="Risicoadres">
          <StringField readonly :value="dekking.adres" />
        </FieldRowV2>
        <FieldRowV2 label="Bouwaard">
          <StringField readonly :value="dekking.omsbouwaardcode" />
        </FieldRowV2>
        <FieldRowV2 label="Bouwaard dak">
          <StringField readonly :value="dekking.omsbouwaarddak" />
        </FieldRowV2>
        <FieldRowV2 label="Bouwaard vloer">
          <StringField readonly :value="dekking.omsbouwaardvloeren" />
        </FieldRowV2>
        <FieldRowV2 label="Bouwaard muren">
          <StringField readonly :value="dekking.omsbouwaardmuren" />
        </FieldRowV2>
        <FieldRowV2 label="Bestemming">
          <StringField readonly :value="dekking.omsbestemmingscode" />
        </FieldRowV2>
        <FieldRowV2 label="Bouwjaar">
          <StringField readonly :value="dekking.bouwjaar" />
        </FieldRowV2>
        <FieldRowV2 label="Soort gebouw">
          <StringField readonly :value="gebouwOms ?? '-'" />
        </FieldRowV2>
        <FieldRowV2 label="Gebruik gebouw">
          <StringField readonly :value="dekking.omsgebruikgebouw" />
        </FieldRowV2>
      </template>
      <template v-if="data.polis.soortverzekering === SoortVerzekering.RE.toUpperCase()">
        <FieldRowV2 label="Aantal reisdagen">
          <StringField readonly :value="dekking.aantalreisdagen" />
        </FieldRowV2>
        <FieldRowV2 label="Dekkingsgebied">
          <StringField readonly :value="dekking.dekkingsgebiedcode" />
        </FieldRowV2>
      </template>
      <template v-if="data.polis.soortverzekering === SoortVerzekering.TR.toUpperCase()">
        <FieldRowV2 label="Soort object">
          <StringField readonly :value="dekking.omssoortvervoer" />
        </FieldRowV2>
        <FieldRowV2 label="Kenteken">
          <span v-if="!dekking.kenteken">-</span>
          <KentekenField
            v-else
            readonly
            :value="dekking.kenteken"
            :additional-data="{
              merk: dekking.merk,
              model: dekking.model,
              bouwjaar: dekking.bouwjaar,
            }"
          />
        </FieldRowV2>
        <FieldRowV2 label="Chassisnummer">
          <StringField readonly :value="dekking.chassisnummer" />
        </FieldRowV2>
        <FieldRowV2 label="Dekkingsgebied">
          <StringField readonly :value="dekking.omsdekkingsgebiedcode" />
        </FieldRowV2>
      </template>
      <template v-if="data.polis.soortverzekering === SoortVerzekering.VA.toUpperCase()">
        <FieldRowV2 label="Soort object">
          <StringField readonly :value="dekking.omsobjectcode" />
        </FieldRowV2>
        <FieldRowV2 label="Kenteken">
          <span v-if="!dekking.kenteken">-</span>
          <KentekenField
            v-else
            readonly
            :value="dekking.kenteken"
            :additional-data="{
              merk: dekking.merk,
              model: dekking.model,
              bouwjaar: dekking.bouwjaar,
            }"
          />
        </FieldRowV2>
        <FieldRowV2 label="Chassisnummer">
          <StringField readonly :value="dekking.chassisnummer" />
        </FieldRowV2>
        <FieldRowV2 label="Dekkingsgebied">
          <StringField readonly :value="dekking.omsdekkingsgebiedcode" />
        </FieldRowV2>
        <FieldRowV2 label="Bouwaard">
          <StringField readonly :value="dekking.omsbouwaardboot" />
        </FieldRowV2>
      </template>
      <template v-if="data.polis.soortverzekering === SoortVerzekering.MR.toUpperCase()">
        <FieldRowV2
          v-if="dekking.geboortedatum && tryFormatDate(dekking.geboortedatum) !== '01-01-1900'"
          label="Geboortedatum jongste bestuurder"
          >{{ tryFormatDate(dekking.geboortedatum) }}</FieldRowV2
        >
        <FieldRowV2 v-else label="Regelmatige bestuurder">
          {{
            `${dekking.bestuurder[0].voorletters} ${dekking.bestuurder[0].naam}  ${tryFormatDate(dekking.bestuurder[0].geboortedatum)}`
          }}
        </FieldRowV2>
      </template>
      <template v-if="data.vrijerubrieken">
        <FieldRowV2
          v-for="[key, value] in Object.entries(data.vrijerubrieken)"
          :key="key"
          :label="key"
        >
          <StringField readonly :value="String(value)" />
        </FieldRowV2>
      </template>
    </FieldRowGroup>
  </div>
</template>
